import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Sun, MapPin, Warning, Sparkle, Notepad, Package,
  ArrowRight, ShoppingCart, X,
} from "@phosphor-icons/react";

/**
 * Morning Materials View (Prep Sheet)
 * Shows tomorrow's assigned jobs + active follow-ups for the current worker.
 * Includes property office notes + on-demand AI shopping list generation.
 */
export default function PrepSheet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [materialsOpen, setMaterialsOpen] = useState(null); // job object

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prep-sheet");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load prep sheet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="mt-6 font-bold text-slate-500">Loading prep sheet…</div>;
  }

  const tomorrow = data?.tomorrow || [];
  const followUps = data?.follow_ups || [];
  const dateStr = data?.date || "";

  const empty = tomorrow.length === 0 && followUps.length === 0;

  return (
    <>
      <div className="mt-4" data-testid="prep-sheet">
        <div className="flex items-center gap-2 mb-1">
          <Sun weight="fill" size={22} className="text-amber-500"/>
          <div className="font-display font-black text-xl uppercase tracking-wide">Tomorrow's Prep</div>
          <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">{dateStr}</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Get materials ready tonight. Tap <span className="inline-flex items-center gap-1 font-black text-[#0f172a]"><Sparkle weight="fill" size={12}/> Suggest</span> to have AI parse the WO + photos into a shopping list.
        </p>

        {empty && (
          <div className="rugged-card p-8 text-center">
            <Package weight="bold" size={40} className="mx-auto opacity-40"/>
            <div className="mt-3 font-display font-black text-2xl">Nothing on deck</div>
            <p className="mt-2 text-slate-500 text-sm">No jobs scheduled for {dateStr} and no active follow-ups.</p>
          </div>
        )}

        {tomorrow.length > 0 && (
          <section className="mb-6">
            <h3 className="font-black uppercase text-xs tracking-widest text-slate-600 mb-2">Scheduled tomorrow · {tomorrow.length}</h3>
            <div className="space-y-3">
              {tomorrow.map(j => <PrepJobCard key={j.id} job={j} onMaterials={() => setMaterialsOpen(j)} onReload={load}/>)}
            </div>
          </section>
        )}

        {followUps.length > 0 && (
          <section>
            <h3 className="font-black uppercase text-xs tracking-widest text-yellow-700 mb-2 inline-flex items-center gap-1.5">
              <Warning weight="fill" size={14}/> Active follow-ups · {followUps.length}
            </h3>
            <div className="space-y-3">
              {followUps.map(j => <PrepJobCard key={j.id} job={j} onMaterials={() => setMaterialsOpen(j)} onReload={load} isFollowUp/>)}
            </div>
          </section>
        )}
      </div>

      {materialsOpen && (
        <MaterialsModal job={materialsOpen} onClose={() => setMaterialsOpen(null)} onReload={load}/>
      )}
    </>
  );
}

function PrepJobCard({ job, onMaterials, isFollowUp }) {
  const hasMaterials = (job.ai_materials_list || []).length > 0;
  return (
    <div className={`rugged-card p-4 border-l-4 ${isFollowUp ? "border-yellow-500" : "border-[#00C4CC]"}`} data-testid={`prep-card-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link to={`/worker/jobs/${job.id}`} className="font-display font-black text-lg tracking-tight hover:underline block truncate">
            {job.title}
          </Link>
          {job.work_order_number && (
            <div className="mt-1 inline-flex items-center px-2 py-0.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
              WO# {job.work_order_number}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600 font-medium">
            <MapPin weight="bold" size={14}/> <span className="truncate">{job.property_address}{job.unit ? ` · #${job.unit}` : ""}</span>
          </div>
          {job.office_notes && (
            <div className="mt-2 p-2 bg-cyan-50 border-l-2 border-cyan-500 text-xs text-slate-700">
              <div className="font-black uppercase tracking-widest text-[9px] text-cyan-700 mb-0.5">Office notes</div>
              {job.office_notes}
            </div>
          )}
          {job.description && (
            <div className="mt-2 text-xs text-slate-500 line-clamp-2">{job.description}</div>
          )}
          {hasMaterials && (
            <div className="mt-2 p-2 bg-amber-50 border-l-2 border-amber-500 text-xs">
              <div className="font-black uppercase tracking-widest text-[9px] text-amber-700 mb-1 inline-flex items-center gap-1">
                <ShoppingCart weight="fill" size={10}/> AI Shopping list · {(job.ai_materials_list || []).length} items
              </div>
              <ul className="space-y-0.5 pl-1">
                {(job.ai_materials_list || []).slice(0, 5).map((it, i) => (
                  <li key={i} className="text-slate-700">• <span className="font-black">{it.qty}</span> {it.item}{it.notes ? <span className="text-slate-500"> — {it.notes}</span> : ""}</li>
                ))}
                {(job.ai_materials_list || []).length > 5 && (
                  <li className="text-slate-500 italic">+ {job.ai_materials_list.length - 5} more…</li>
                )}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          <button onClick={onMaterials} data-testid={`suggest-materials-${job.id}`}
            className="h-9 px-3 bg-gradient-to-r from-[#0f172a] to-[#00C4CC] text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 whitespace-nowrap">
            <Sparkle weight="fill" size={12}/> {hasMaterials ? "Regen" : "Suggest"}
          </button>
          <Link to={`/worker/jobs/${job.id}`} data-testid={`prep-open-${job.id}`}
            className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 hover:bg-slate-50">
            Open <ArrowRight weight="bold" size={12}/>
          </Link>
        </div>
      </div>
    </div>
  );
}

function MaterialsModal({ job, onClose, onReload }) {
  const [items, setItems] = useState(job.ai_materials_list || []);
  const [summary, setSummary] = useState(job.ai_materials_summary || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/jobs/${job.id}/suggest-materials`);
      setItems(r.data.items || []);
      setSummary(r.data.summary || "");
      toast.success("Materials suggested & saved");
      onReload();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "AI suggestion failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border-4 border-[#0f172a] shadow-[6px_6px_0px_#00C4CC] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="materials-modal">
        <div className="p-4 border-b-2 border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Shopping list · AI</div>
            <div className="font-display font-black text-xl">{job.title}</div>
          </div>
          <button onClick={onClose} className="p-1"><X weight="bold" size={22}/></button>
        </div>
        <div className="p-4 space-y-3">
          {items.length === 0 && !busy && (
            <div className="text-sm text-slate-500">
              No suggestions yet. Tap <b>Suggest with AI</b> to parse the work order + photos into a materials list.
            </div>
          )}
          {summary && (
            <div className="p-2 bg-slate-50 border-l-2 border-[#0f172a] text-xs text-slate-700">
              <div className="font-black uppercase text-[9px] tracking-widest text-slate-600 mb-0.5">Summary</div>
              {summary}
            </div>
          )}
          {items.length > 0 && (
            <ul className="space-y-2">
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 p-2 border-2 border-slate-200 rounded-sm" data-testid={`material-item-${i}`}>
                  <span className="mt-0.5 inline-flex items-center h-5 px-1.5 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest">{it.qty}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm">{it.item}</div>
                    {it.notes && <div className="text-xs text-slate-500 mt-0.5">{it.notes}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <div className="text-xs text-red-600 font-bold">{error}</div>}
          <button onClick={generate} disabled={busy}
            data-testid="materials-generate-btn"
            className="w-full h-12 bg-gradient-to-r from-[#0f172a] to-[#00C4CC] text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkle weight="fill" size={16}/> {busy ? "Generating…" : (items.length ? "Regenerate with AI" : "Suggest with AI")}
          </button>
        </div>
      </div>
    </div>
  );
}
